import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteConfirmationDialog from '../../components/common/DeleteConfirmationDialog';
import { toast } from '../../components/toast';
import { getUserGroups } from '../../utils';
import { useInventoryOperations } from '../../hooks/useInventoryOperations';
import { inventoryValidationSchema } from '../../utils/inventoryValidation';
import { inventoryAssignmentValidationSchema } from '../../utils/inventoryAssignmentValidation';
import {
  getInitialInventoryFormData,
  formatInventoryDataForSubmission,
  getInventoryFormDataFromRecord,
} from '../../utils/inventoryUtils';
import {
  getInitialInventoryAssignmentFormDataWithItem,
  formatInventoryAssignmentDataForSubmission,
} from '../../utils/inventoryAssignmentUtils';
import { createInventoryAssignment } from '../../services/api';
import { HRPageHeader } from '../../components/hr';
import { LightTooltip } from '../../components/custom-components/custom-tooltip';
import {
  InventoryStatsCards,
  InventoryFilters,
  InventoryTable,
  InventoryForm,
  QuickAssignmentDialog,
} from '../../components/inventory';

const allowedGroups = ['admin', 'hr'];

export default function InventoryManagementPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('add');
  const [editingItem, setEditingItem] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [openAssignmentDialog, setOpenAssignmentDialog] = useState(false);
  const [itemToAssign, setItemToAssign] = useState(null);
  const [assignmentFormData, setAssignmentFormData] = useState({});
  const [assignmentFormErrors, setAssignmentFormErrors] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [nameFilter, setNameFilter] = useState('All');
  const [minQuantity, setMinQuantity] = useState('');
  const [maxQuantity, setMaxQuantity] = useState('');
  const [addedByFilter, setAddedByFilter] = useState('All');

  const {
    isLoading,
    inventoryItems,
    stats,
    totalCount,
    filterOptions,
    loadInventoryItems,
    createInventoryItemRecord,
    updateInventoryItemRecord,
    deleteInventoryItemRecord,
  } = useInventoryOperations();

  const [formData, setFormData] = useState(getInitialInventoryFormData());
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    const userGroups = getUserGroups();
    if (userGroups === null || !allowedGroups.some((group) => userGroups.includes(group))) {
      navigate('/dashboard', { replace: true });
    }
  }, [navigate]);
  // Load inventory items when filters change
  useEffect(() => {
    loadInventoryItems(page, rowsPerPage, searchTerm, nameFilter, minQuantity, maxQuantity, addedByFilter);
  }, [loadInventoryItems, page, rowsPerPage, searchTerm, nameFilter, minQuantity, maxQuantity, addedByFilter]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0); // Reset to first page when searching
  };

  const handleFilterChange = (filterType, value) => {
    switch (filterType) {
      case 'name':
        setNameFilter(value);
        break;
      case 'minQuantity':
        setMinQuantity(value);
        break;
      case 'maxQuantity':
        setMaxQuantity(value);
        break;
      case 'addedBy':
        setAddedByFilter(value);
        break;
      default:
        break;
    }
    setPage(0); // Reset to first page when filtering
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setNameFilter('All');
    setMinQuantity('');
    setMaxQuantity('');
    setAddedByFilter('All');
    setPage(0);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear error for this field when user starts typing
    if (formErrors[field]) {
      setFormErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const validateForm = async () => {
    try {
      await inventoryValidationSchema.validate(formData, { abortEarly: false });
      setFormErrors({});
      return true;
    } catch (error) {
      const errors = {};
      error.inner.forEach((err) => {
        errors[err.path] = err.message;
      });
      setFormErrors(errors);
      return false;
    }
  };

  const handleSubmit = async () => {
    const isValid = await validateForm();
    if (!isValid) return;

    const submissionData = formatInventoryDataForSubmission(formData);
    let success = false;

    if (dialogMode === 'add') {
      success = await createInventoryItemRecord(submissionData);
    } else if (dialogMode === 'edit') {
      success = await updateInventoryItemRecord(editingItem.id, submissionData);
    }

    if (success) {
      setOpenDialog(false);
      setFormData(getInitialInventoryFormData());
      setFormErrors({});
      setEditingItem(null);
      // Reload data
      loadInventoryItems(page, rowsPerPage, searchTerm, nameFilter, minQuantity, maxQuantity, addedByFilter);
    }
  };

  const handleAddItem = () => {
    setDialogMode('add');
    setFormData(getInitialInventoryFormData());
    setFormErrors({});
    setEditingItem(null);
    setOpenDialog(true);
  };

  const handleViewItem = (item) => {
    setDialogMode('view');
    setFormData(getInventoryFormDataFromRecord(item));
    setFormErrors({});
    setEditingItem(item);
    setOpenDialog(true);
  };

  const handleEditItem = (item) => {
    setDialogMode('edit');
    setFormData(getInventoryFormDataFromRecord(item));
    setFormErrors({});
    setEditingItem(item);
    setOpenDialog(true);
  };

  const handleDeleteItem = (item) => {
    setItemToDelete(item);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;

    const success = await deleteInventoryItemRecord(itemToDelete.id);
    if (success) {
      setOpenDeleteDialog(false);
      setItemToDelete(null);
      // Reload data
      loadInventoryItems(page, rowsPerPage, searchTerm, nameFilter, minQuantity, maxQuantity, addedByFilter);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormData(getInitialInventoryFormData());
    setFormErrors({});
    setEditingItem(null);
  };

  // Assignment functions
  const handleAssignItem = (item) => {
    setItemToAssign(item);
    setAssignmentFormData(getInitialInventoryAssignmentFormDataWithItem(item));
    setAssignmentFormErrors({});
    setOpenAssignmentDialog(true);
  };

  const handleCloseAssignmentDialog = () => {
    setOpenAssignmentDialog(false);
    setItemToAssign(null);
    setAssignmentFormData({});
    setAssignmentFormErrors({});
  };

  const handleAssignmentInputChange = (field, value) => {
    setAssignmentFormData((prev) => ({ ...prev, [field]: value }));
    if (assignmentFormErrors[field]) {
      setAssignmentFormErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validateAssignmentForm = async () => {
    try {
      await inventoryAssignmentValidationSchema.validate(assignmentFormData, { abortEarly: false });
      setAssignmentFormErrors({});
      return true;
    } catch (error) {
      const errors = {};
      error.inner.forEach((err) => {
        if (err.path) {
          errors[err.path] = err.message;
        }
      });
      setAssignmentFormErrors(errors);
      return false;
    }
  };

  const handleAssignmentSubmit = async () => {
    const isValid = await validateAssignmentForm();
    if (!isValid) {
      toast.error('Please fill in all required fields correctly');
      return;
    }

    try {
      const payload = formatInventoryAssignmentDataForSubmission(assignmentFormData);
      await createInventoryAssignment(payload);
      toast.success('Item assigned successfully!');
      handleCloseAssignmentDialog();
      // Reload inventory data to update quantities
      loadInventoryItems(page, rowsPerPage, searchTerm, nameFilter, minQuantity, maxQuantity, addedByFilter);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to assign item');
    }
  };

  return (
    <>
      <Helmet>
        <title>Inventory Management | BBMS</title>
      </Helmet>

      <HRPageHeader
        title="Inventory management"
        description="Catalog equipment and supplies, monitor stock levels, and add items before assigning them to employees."
        actions={
          <LightTooltip title="Add a new inventory item to the catalog.">
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddItem}>
              Add item
            </Button>
          </LightTooltip>
        }
      />

      <InventoryStatsCards stats={stats} isLoading={isLoading} />

      <InventoryFilters
        searchTerm={searchTerm}
        nameFilter={nameFilter}
        minQuantity={minQuantity}
        maxQuantity={maxQuantity}
        addedByFilter={addedByFilter}
        filterOptions={filterOptions}
        onSearchChange={handleSearchChange}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
      />

      <InventoryTable
        inventoryItems={inventoryItems}
        isLoading={isLoading}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={totalCount}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        onViewItem={handleViewItem}
        onEditItem={handleEditItem}
        onDeleteItem={handleDeleteItem}
        onAssignItem={handleAssignItem}
      />

      <InventoryForm
        open={openDialog}
        mode={dialogMode}
        formData={formData}
        formErrors={formErrors}
        onClose={handleCloseDialog}
        onSubmit={handleSubmit}
        onInputChange={handleInputChange}
      />

      <QuickAssignmentDialog
        open={openAssignmentDialog}
        onClose={handleCloseAssignmentDialog}
        onSubmit={handleAssignmentSubmit}
        inventoryItem={itemToAssign}
        formData={assignmentFormData}
        formErrors={assignmentFormErrors}
        onInputChange={handleAssignmentInputChange}
      />

      <DeleteConfirmationDialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Inventory Item"
        message={`Are you sure you want to delete "${itemToDelete?.name}"? This action cannot be undone.`}
      />
    </>
  );
}
